import {
  Component,
  input,
  output,
  computed,
  ChangeDetectionStrategy,
  effect,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SoSvgIconUI } from '../so-svg-icon/so-svg-icon.component';

@Component({
  selector: 'so-pagination-ui',
  standalone: true,
  imports: [CommonModule, SoSvgIconUI],
  templateUrl: './so-pagination.component.html',
  styleUrl: './so-pagination.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SoPaginationUI {
  // Inputs
  totalItems = input<number>(0);
  itemsPerPage = input<number>(10);
  currentPage = input<number>(1);
  showText = input<boolean>(true);
  paddingEnd = input<string>('2rem');
  loadingItems = input<boolean>(false);
  prevDisabled = input<boolean>(false);
  nextDisabled = input<boolean>(false);

  // Textos personalizables
  textShowingRecords = input<string>('Mostrando registros del %s al %s de un total de %s registros.');
  textShowingRecordsAndPages = input<string>('Mostrando registros del %s al %s de un total de %s en %s páginas.');

  // Output
  pageChanged = output<number>();

  // Internal state
  protected internalCurrentPage = signal<number>(1);

  constructor() {
    // Sync input currentPage with internal state
    effect(() => {
      this.internalCurrentPage.set(this.currentPage());
    }, {  });
  }

  // Computed values
  protected totalPages = computed(() => {
    const items = this.itemsPerPage();
    return items === 0 ? 0 : Math.ceil(this.totalItems() / items);
  });

  protected firstIndex = computed(() => {
    const lastIdx = this.lastIndex();
    return lastIdx > 0 ? (this.internalCurrentPage() - 1) * this.itemsPerPage() + 1 : 0;
  });

  protected lastIndex = computed(() => {
    return Math.min(this.internalCurrentPage() * this.itemsPerPage(), this.totalItems());
  });

  protected displayText = computed(() => {
    const totalPages = this.totalPages();
    const first = this.firstIndex();
    const last = this.lastIndex();
    const total = this.totalItems();

    if (totalPages <= 1) {
      return this.formatString(this.textShowingRecords(), first, last, total);
    } else {
      return this.formatString(this.textShowingRecordsAndPages(), first, last, total, totalPages);
    }
  });

  protected textParts = computed(() => {
    const text = this.displayText();
    const totalPages = this.totalPages();
    const loadingRegEx: RegExp = totalPages <= 1 
      ? /(.+\D+)(\d+)(\D+)$/ 
      : /(.+\D+)(\d+)(\D+)(\d+)(\D+)$/;

    if (!this.loadingItems()) {
      return { full: this.addCommas(text), parts: null };
    }

    const matches = text.match(loadingRegEx);
    
    if (totalPages <= 1 && matches && matches.length === 4) {
      return {
        full: null,
        parts: {
          part1: this.addCommas(matches[1]),
          part2: this.addCommas(matches[3]),
          part3: null
        }
      };
    } else if (totalPages > 1 && matches && matches.length === 6) {
      return {
        full: null,
        parts: {
          part1: this.addCommas(matches[1]),
          part2: this.addCommas(matches[3]),
          part3: this.addCommas(matches[5])
        }
      };
    }

    return { full: this.addCommas(text), parts: null };
  });

  protected isPrevDisabled = computed(() => {
    return this.prevDisabled() || this.internalCurrentPage() === 1;
  });

  protected isNextDisabled = computed(() => {
    return this.nextDisabled() || this.internalCurrentPage() >= this.totalPages();
  });

  protected showPrevPage = computed(() => {
    return this.internalCurrentPage() > 1;
  });

  protected showNextPage = computed(() => {
    return this.internalCurrentPage() < this.totalPages();
  });

  protected prevPageNumber = computed(() => {
    return this.internalCurrentPage() - 1;
  });

  protected nextPageNumber = computed(() => {
    return this.internalCurrentPage() + 1;
  });

  // Methods
  goToPage(page: number): void {
    const total = this.totalPages();
    if (page >= 1 && page <= total) {
      this.internalCurrentPage.set(page);
      this.pageChanged.emit(page);
    }
  }

  goToPreviousPage(): void {
    if (this.internalCurrentPage() > 1) {
      this.goToPage(this.internalCurrentPage() - 1);
    }
  }

  goToNextPage(): void {
    if (this.internalCurrentPage() < this.totalPages()) {
      this.goToPage(this.internalCurrentPage() + 1);
    }
  }

  goToFirstPage(): void {
    this.goToPage(1);
  }

  goToLastPage(): void {
    this.goToPage(this.totalPages());
  }

  private formatString(text: string, ...args: any[]): string {
    let i = 0;
    return text.replace(/%s/g, () => args[i++] ?? '');
  }

  private addCommas(value: string | number): string {
    return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
}
